/**
 * Contraste de texto sobre fondos de color arbitrarios.
 *
 * Los chips de categoría (deporte, tipo de fichero, módulo, grupo…) sacan su
 * fondo de una tabla de ~125 colores y le ponían `color: 'white'` encima sin
 * mirar. Con eso, 117 de esos 125 fondos dejaban el rótulo por debajo de
 * 4,5:1 — «PDF» en blanco sobre `#39d0a1` se quedaba en 1,96:1.
 *
 * El sistema de diseño resuelve esto con pares superficie/texto, pero aquí el
 * fondo no lo elige el sistema: lo elige el dato. Así que en vez de re-colorear
 * 125 categorías a mano, se calcula el texto a partir del fondo — que es la
 * misma regla de `--on-brand`, generalizada: encima del relleno va lo que se
 * lea.
 */

/** Tinta de marca (`primary.contrastText`). */
const INK = '#12150f';
const WHITE = '#ffffff';

type Rgb = [number, number, number];

/**
 * Un color tal y como llega del dato: puede no estar. Los chips de categoría
 * resuelven su color contra una tabla de constantes, y hasta que ese `find`
 * responde —o si el id no está en la tabla— aquí no llega nada.
 */
export type MaybeColor = string | null | undefined;

function parseColor(color: MaybeColor): Rgb | null {
    if (typeof color !== 'string') return null;

    const hex = color.trim();

    const corto = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hex);
    if (corto) {
        return [
            parseInt(corto[1]! + corto[1]!, 16),
            parseInt(corto[2]! + corto[2]!, 16),
            parseInt(corto[3]! + corto[3]!, 16),
        ];
    }

    const largo = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    if (largo) {
        return [
            parseInt(largo[1]!, 16),
            parseInt(largo[2]!, 16),
            parseInt(largo[3]!, 16),
        ];
    }

    const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(hex);
    if (rgb) {
        return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    }

    return null;
}

/** Luminancia relativa de WCAG 2.1. */
function luminance([r, g, b]: Rgb): number {
    const lin = [r, g, b].map((v) => {
        const c = v / 255;
        return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    }) as Rgb;

    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** Ratio de contraste entre dos colores, de 1:1 a 21:1. */
export function contrastRatio(a: MaybeColor, b: MaybeColor): number {
    const ca = parseColor(a);
    const cb = parseColor(b);
    if (!ca || !cb) return 1;

    const la = luminance(ca);
    const lb = luminance(cb);

    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * El color de texto que se lee sobre `background`: la tinta de marca o el
 * blanco, el que más contraste dé. Si no se puede interpretar el fondo,
 * devuelve la tinta, que es lo que se lee sobre la mayoría de los fondos
 * claros de la aplicación.
 */
export function readableOn(background: MaybeColor): string {
    if (!parseColor(background)) return INK;

    return contrastRatio(INK, background) >= contrastRatio(WHITE, background)
        ? INK
        : WHITE;
}

/**
 * Fondo y texto de un chip de categoría, ya legibles.
 *
 * Con elegir entre tinta y blanco, 117 de los 125 colores de categoría pasan
 * 4,5:1. Los 8 que quedan son tonos medios donde ninguno de los dos llega
 * (4,1-4,5:1), así que a esos se les oscurece el fondo a pasos hasta que el
 * blanco entra — el tono se reconoce igual y el rótulo se lee.
 */
export function chipColors(background: MaybeColor): {
    backgroundColor: string | undefined;
    color: string;
} {
    const rgb = parseColor(background);
    if (!rgb) {
        // Sin fondo que medir no hay nada que derivar. La cadena se deja tal
        // cual —puede ser un token que MUI resuelva en `sx`, o una palabra
        // clave de CSS— y encima va la tinta, que es lo que se lee sobre las
        // superficies claras de la aplicación.
        return {
            backgroundColor:
                typeof background === 'string' ? background : undefined,
            color: INK,
        };
    }

    let fondo = rgb;
    let texto = readableOn(toHex(fondo));

    // Máximo 6 pasos: suficiente para llevar cualquier tono medio a 4,5:1 sin
    // que deje de reconocerse su color.
    for (let i = 0; i < 6; i++) {
        if (contrastRatio(texto, toHex(fondo)) >= 4.5) break;
        fondo = fondo.map((v) => Math.round(v * 0.88)) as Rgb;
        texto = WHITE;
    }

    return { backgroundColor: toHex(fondo), color: texto };
}

/**
 * El mismo color, oscurecido a pasos hasta que se lee sobre `sobre` (blanco por
 * defecto). Para cuando el color de categoría tiene que hacer de LETRA y no de
 * relleno — la inicial dentro del círculo claro de un chip, por ejemplo: la
 * naranja `#ff9800` sobre blanco se queda en 2:1.
 *
 * Conserva el tono, así que la categoría se sigue reconociendo por el color.
 */
export function onLight(color: MaybeColor, sobre = '#ffffff'): string {
    const rgb = parseColor(color);
    if (!rgb) return INK;

    let actual = rgb;
    for (let i = 0; i < 12; i++) {
        if (contrastRatio(toHex(actual), sobre) >= 4.5) break;
        actual = actual.map((v) => Math.round(v * 0.85)) as Rgb;
    }

    return toHex(actual);
}

function toHex([r, g, b]: Rgb): string {
    return (
        '#' +
        [r, g, b]
            .map((v) =>
                Math.max(0, Math.min(255, Math.round(v)))
                    .toString(16)
                    .padStart(2, '0'),
            )
            .join('')
    );
}
